import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as AllowIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import SettingsErrorAlert from './SettingsErrorAlert';

const IpAccessSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [allow, setAllow] = useState([]);
  const [deny, setDeny] = useState([]);
  const [clientIp, setClientIp] = useState('');
  const [draftIp, setDraftIp] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [draftList, setDraftList] = useState('allow');

  const applyPayload = (data = {}) => {
    setEnabled(Boolean(data.enabled));
    setAllow(data.allow || []);
    setDeny(data.deny || []);
    setClientIp(data.client_ip || '');
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/settings/ip-access');
      applyPayload(response.data.data || {});
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load IP allow/deny settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleEnabled = async (next) => {
    if (!canEdit) return;
    if (next && clientIp && allow.length > 0 && !allow.some((e) => e.ip === clientIp)) {
      const proceed = window.confirm(
        `Your current server-seen IP is ${clientIp}.\n\n`
        + 'It does not appear as an exact match on the Allow list. '
        + 'If you continue and that IP is not covered by a CIDR, you will be locked out.\n\n'
        + 'Click Cancel, use “Add current IP to Allow”, then enable again.'
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const response = await api.put('/settings/ip-access/enabled', { enabled: next });
      applyPayload(response.data.data || {});
      toast.success(next ? 'IP access control enabled' : 'IP access control disabled');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async () => {
    if (!canEdit) return;
    const ip = draftIp.trim();
    if (!ip) {
      toast.error('Enter an IP address or CIDR');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/settings/ip-access/entries', {
        list: draftList,
        ip,
        label: draftLabel.trim()
      });
      applyPayload(response.data.data || {});
      setDraftIp('');
      setDraftLabel('');
      toast.success(draftList === 'deny' ? 'IP blocked' : 'IP allowed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add IP');
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (list, id) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const response = await api.delete(`/settings/ip-access/entries/${list}/${id}`);
      applyPayload(response.data.data || {});
      toast.success('IP entry removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove IP');
    } finally {
      setSaving(false);
    }
  };

  const renderTable = (rows, list) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>IP / CIDR</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Added</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">
                  No entries yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700 }}>
                  {row.ip}
                </TableCell>
                <TableCell>{row.label || '—'}</TableCell>
                <TableCell>
                  {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={saving}
                        onClick={() => removeEntry(list, row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <SecurityIcon sx={{ color: '#b45309', fontSize: 34 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              IP Allow / Deny List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Control which client IP addresses may access TenantHub after login.
              Blocked IPs are always denied. When the allow list has entries, only those IPs may sign in.
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={saving}>Refresh</Button>
      </Box>

      {error && (
        <SettingsErrorAlert message={error.message} path={error.path} status={error.status} />
      )}

      <Alert severity="warning" sx={{ mb: 2 }}>
        Your current IP as seen by the server is
        {' '}
        <strong style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{clientIp || 'unknown'}</strong>
        .
        {' '}
        Use this exact value on the allow list — not your ISP “public IP” — when you access via
        localhost, Docker Desktop, or kubectl port-forward (those usually show
        {' '}
        <code>127.0.0.1</code>
        ).
        {' '}
        {canEdit && clientIp ? (
          <Button
            size="small"
            sx={{ ml: 1, verticalAlign: 'baseline' }}
            disabled={saving || allow.some((e) => e.ip === clientIp)}
            onClick={async () => {
              setSaving(true);
              try {
                const response = await api.post('/settings/ip-access/entries', {
                  list: 'allow',
                  ip: clientIp,
                  label: 'This session'
                });
                applyPayload(response.data.data || {});
                toast.success(`Added ${clientIp} to allow list`);
              } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to add current IP');
              } finally {
                setSaving(false);
              }
            }}
          >
            Add current IP to Allow
          </Button>
        ) : null}
      </Alert>

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <FormControlLabel
          control={(
            <Switch
              checked={enabled}
              disabled={!canEdit || saving}
              onChange={(e) => toggleEnabled(e.target.checked)}
            />
          )}
          label={(
            <Box>
              <Typography fontWeight={700}>
                Enforce IP access control
                {' '}
                <Chip
                  size="small"
                  label={enabled ? 'ON' : 'OFF'}
                  color={enabled ? 'warning' : 'default'}
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                When ON: deny list blocks access; allow list (if any) is required. When OFF: all IPs may access.
              </Typography>
            </Box>
          )}
        />
      </Paper>

      {canEdit && (
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
            Add IP
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>List</InputLabel>
              <Select
                label="List"
                value={draftList}
                onChange={(e) => setDraftList(e.target.value)}
              >
                <MenuItem value="allow">Allow</MenuItem>
                <MenuItem value="deny">Deny / Block</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="IP or CIDR"
              value={draftIp}
              onChange={(e) => setDraftIp(e.target.value)}
              placeholder="203.0.113.10 or 203.0.113.0/24"
              sx={{ flex: '1 1 220px' }}
            />
            <TextField
              size="small"
              label="Label (optional)"
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="Office VPN"
              sx={{ flex: '1 1 180px' }}
            />
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={addEntry}
              disabled={saving}
              sx={{ background: draftList === 'deny'
                ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
                : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' }}
            >
              {draftList === 'deny' ? 'Block IP' : 'Allow IP'}
            </Button>
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <AllowIcon sx={{ color: '#0f766e' }} />
        <Typography variant="h6" fontWeight={800}>Allow list</Typography>
        <Chip size="small" label={`${allow.length}`} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        If this list is empty while enforcement is ON, any IP that is not blocked may access the app.
        If it has entries, only matching IPs may access.
      </Typography>
      {renderTable(allow, 'allow')}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <BlockIcon sx={{ color: '#b91c1c' }} />
        <Typography variant="h6" fontWeight={800}>Deny / Block list</Typography>
        <Chip size="small" label={`${deny.length}`} color="error" variant="outlined" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        These IPs are always refused, even if they also appear on the allow list.
      </Typography>
      {renderTable(deny, 'deny')}
    </Box>
  );
};

export default IpAccessSettings;
